import { useState, useEffect, useRef } from 'react';
import { getServiceOptions, extractTitle, extractPrice } from '@/screens/services/NewServices';
import { getBookingConfig } from '@/screens/services/BookingConfig';
import { whatsappConfig } from '@/screens/services/WhatsappConfig';
import CarpetCalculator from './CarpetCalculator';
import SanitiseCalculator from './SanitiseCalculator';
import PestControlBooking from './PestControlBooking';
import PestControlNotice from './PestControlNotice';
import AircondCalculator from './AircondCalculator';
import TransportationFee from './TransportationFee';
import { PriceCalculator, type ServiceWithPrice } from '../utils/PriceCalculator';
import { useTranslation } from 'react-i18next';

interface BookingModalProps {
    isOpen: boolean;
    onClose: () => void;
    serviceId: string;
    selectedServices?: ServiceWithPrice[];
}

const formatTextWithItalics = (text: string) => {
    // Check if text contains backticks
    if (!text.includes('`')) return text;
    
    // Split the text by backticks
    const parts = text.split('`');
    const result: React.ReactNode[] = [];
    
    // Alternate between normal and italic text
    parts.forEach((part, index) => {
        if (index % 2 === 0) {
            // Even indices are regular text
            result.push(part);
        } else {
            // Odd indices should be italic
            result.push(<i key={index}>{part}</i>);
        }
    });
    
    return result;
};

const BookingModal = ({ isOpen, onClose, serviceId, selectedServices = [] }: BookingModalProps) => {
    const { t } = useTranslation();
    const bookingConfig = getBookingConfig(t);
    const serviceOptions = getServiceOptions(t);
    const [formData, setFormData] = useState({
        name: '',
        date: '',
        address: '',
        selectedServices: new Set<ServiceWithPrice>()
    });
    const [hasAnyPestControlSelection, setHasAnyPestControlSelection] = useState(false);
    const aircondCalculatorRef = useRef<{ resetUnits: () => void }>(null);

    // Add transportation fee automatically for fabric and sanitise services, but only when needed
    useEffect(() => {
        if ((serviceId === 'fabric' || serviceId === 'sanitise') && formData.selectedServices.size > 0) {
            // Only make changes if no transportation fee already exists
            const hasFee = Array.from(formData.selectedServices).some(service => 
                service.title.includes(t('fees.title')));
            
            // Add the transportation fee if not already present
            if (!hasFee) {
                // Use a functional update to avoid dependency on formData
                setFormData(prev => {
                    // Skip if no changes needed (to prevent unnecessary re-renders)
                    if (Array.from(prev.selectedServices).some(s => s.title.includes(t('fees.title')))) {
                        return prev;
                    }
                    
                    const newServices = new Set(prev.selectedServices);
                    newServices.add({ 
                        title: `${t('fees.title')} (Klang/Selangor)`, 
                        price: 'RM25.00' 
                    });
                    return { ...prev, selectedServices: newServices };
                });
            }
        }
    }, [serviceId, formData.selectedServices.size]); // Add formData.selectedServices.size to dependencies

    useEffect(() => {
        setFormData(prev => {
            const newState = {
                ...prev,
                selectedServices: new Set(selectedServices)
            };
            return newState;
        });
    }, [selectedServices]);

    const handleServiceToggle = (title: string, price: string) => {
        setFormData(prev => {
            const newServices = new Set(prev.selectedServices);
            const existingService = Array.from(newServices)
                .find(s => s.title === title);
            
            if (existingService) {
                newServices.delete(existingService);
            } else {
                newServices.add({ title, price });
            }
            
            return { ...prev, selectedServices: newServices };
        });
    };

    const handleCarpetCalculation = (carpetService: { title: string; price: string }) => {
        setFormData(prev => {
            const newServices = new Set(prev.selectedServices);
            // Remove any existing carpet service
            Array.from(newServices).forEach(service => {
                if (service.title.includes('Karpet')) {
                    newServices.delete(service);
                }
            });
            // Add new carpet service
            newServices.add(carpetService);
            return { ...prev, selectedServices: newServices };
        });
    };

    const handleTransportationFee = (feeService: { title: string; price: string }) => {
        setFormData(prev => {
            const newServices = new Set(prev.selectedServices);
            // Remove any existing transportation fee
            Array.from(newServices).forEach(service => {
                if (service.title.includes(t('fees.title'))) {
                    newServices.delete(service);
                }
            });
            // Add new transportation fee
            newServices.add(feeService);
            return { ...prev, selectedServices: newServices };
        });
    };

    const handleSanitiseCalculation = (sanitiseService: { title: string; price: string }) => {
        setFormData(prev => {
            const newServices = new Set(prev.selectedServices);
            // Remove any existing sanitise service for this premise type
            Array.from(newServices).forEach(service => {
                if (service.title.includes(sanitiseService.title.split('(')[0].trim())) {
                    newServices.delete(service);
                }
            });
            // Add new sanitise service
            newServices.add(sanitiseService);
            return { ...prev, selectedServices: newServices };
        });
    };

    const handlePestControlSelection = (service: { title: string; price: string }, isSelected: boolean) => {
        setFormData(prev => {
            const newServices = new Set(prev.selectedServices);
            if (isSelected) {
                newServices.add(service);
            } else {
                // Remove the service if deselected
                Array.from(newServices).forEach(existing => {
                    if (existing.title.startsWith(service.title.split(' ')[0])) {
                        newServices.delete(existing);
                    }
                });
            }
            return { ...prev, selectedServices: newServices };
        });
    };

    const handlePestControlSelectionChange = (hasSelection: boolean) => {
        setHasAnyPestControlSelection(hasSelection || formData.selectedServices.size > 0);
    };

    const resetForm = () => {
        setFormData({
            name: '',
            date: '',
            address: '',
            selectedServices: new Set<ServiceWithPrice>()
        });
        if (serviceId === 'aircond' && aircondCalculatorRef.current) {
            aircondCalculatorRef.current.resetUnits();
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name || !formData.date || !formData.address || formData.selectedServices.size === 0) {
            alert(t('booking.validationError'));
            return;
        }

        // Calculate total price with range support
        const totalPriceRange = PriceCalculator.calculateTotalRange(formData.selectedServices);

        // Create booking details with total
        const bookingDetails = {
            name: formData.name,
            date: formData.date,
            address: formData.address,
            mainService: serviceId,
            selectedServices: formData.selectedServices,
            totalPrice: PriceCalculator.formatPriceRange(totalPriceRange)
        };

        // Pass to WhatsApp config
        const message = whatsappConfig.formatMessage.booking(bookingDetails);
        whatsappConfig.openWhatsApp(message);

        // Reset form data before closing
        setFormData({
            name: '',
            date: '',
            address: '',
            selectedServices: new Set<ServiceWithPrice>()
        });
        onClose();
    };

    const formatServicesList = () => {
        const services = Array.from(formData.selectedServices);
        const hasAircondServices = services.some(service => service.title.includes('Penghawa Dingin') || service.title.includes('Air Conditioning'));

        return (
            <div className="space-y-2">
                {/* Only show heading for aircond services */}
                {hasAircondServices && <p className="text-sm font-semibold">{t('booking.serviceSelection')}</p>}
                
                {services.map((service, index) => {
                    return (
                        <div
                            key={index}
                            className="px-3 py-2 rounded-md text-sm flex justify-between items-center bg-blue-700 border border-blue-200 text-white"
                        >
                            <span>{formatTextWithItalics(service.title)}</span>
                            <span>{service.price}</span>
                        </div>
                    );
                })}
            </div>
        );
    };

    const handleQuantityChange = (title: string, quantity: number) => {
        setFormData(prev => {
            const newServices = new Set<ServiceWithPrice>();
            Array.from(prev.selectedServices).forEach(service => {
                if (service.title === title) {
                    newServices.add({ ...service, quantity: Math.max(1, quantity) });
                } else {
                    newServices.add(service);
                }
            });
            return { ...prev, selectedServices: newServices };
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-4 md:p-8 w-full max-w-[95%] md:max-w-[560px] max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl md:text-2xl font-semibold">{bookingConfig.title}</h2>
                    <button 
                        onClick={handleClose}
                        className="text-gray-500 hover:text-gray-700"
                        aria-label="Close modal"
                    >
                        ✕
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-gray-700 mb-2">
                                {bookingConfig.fields.name.label}
                            </label>
                            <input
                                type="text"
                                placeholder={bookingConfig.fields.name.placeholder}
                                className="w-full border rounded-md p-2 h-10"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required={bookingConfig.fields.name.required}
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 mb-2">
                                {bookingConfig.fields.dateTime.label}
                            </label>
                            <div className="relative">
                                <input
                                    type="datetime-local"
                                    value={formData.date}
                                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                    className={`w-full border rounded-md p-2 h-10 text-gray-700 ${
                                        !formData.date ? 'text-transparent' : ''
                                    }`}
                                    min={new Date().toISOString().slice(0, 16)}
                                    required={bookingConfig.fields.dateTime.required}
                                />
                                {!formData.date && (
                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm">
                                        {bookingConfig.fields.dateTime.placeholder}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-gray-700 mb-2">
                            {bookingConfig.fields.address.label}
                        </label>
                        <textarea
                            placeholder={bookingConfig.fields.address.placeholder}
                            className="w-full border rounded-md p-2 h-24 resize-none"
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            required={bookingConfig.fields.address.required}
                        />
                    </div>

                    <div>
                        <label className="block text-gray-700 mb-2">
                            {bookingConfig.fields.services.label}
                        </label>
                        <div className="border rounded-md p-3">
                            {/* Only show selected services for aircond */}
                            {serviceId === 'aircond' && formData.selectedServices.size > 0 && (
                                <div className="mb-4 border-b pb-4">
                                    {formatServicesList()}
                                </div>
                            )}

                            {/* Only show options section if service has options */}
                            {serviceOptions[serviceId as keyof typeof serviceOptions]?.options &&
                             Array.isArray(serviceOptions[serviceId as keyof typeof serviceOptions].options) &&
                             serviceOptions[serviceId as keyof typeof serviceOptions].options.length > 0 && (
                                <div className="mb-3">
                                    <p className="font-medium mb-2 text-sm">
                                        {serviceOptions[serviceId as keyof typeof serviceOptions].category}
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {serviceOptions[serviceId as keyof typeof serviceOptions].options.map((option, index) => {
                                            const title = extractTitle(option);
                                            const price = extractPrice(option);
                                            const selectedService = Array.from(formData.selectedServices)
                                                .find(s => s.title === title);

                                            return (
                                                <div key={index} className="flex items-center gap-2 mb-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleServiceToggle(title, price)}
                                                        className={`px-3 py-1.5 rounded-md text-xs flex items-center ${
                                                            selectedService ? 'bg-blue-800 text-white' : 'bg-gray-100 text-gray-700'
                                                        }`}
                                                    >
                                                        <span>{formatTextWithItalics(title)} ({price})</span>
                                                    </button>
                                                    {selectedService && (
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            value={selectedService.quantity ?? 1}
                                                            onChange={e => handleQuantityChange(title, parseInt(e.target.value, 10))}
                                                            className="w-10 h-7 border border-gray-300 rounded-md text-center text-xs font-normal ml-1"
                                                            style={{ padding: 0 }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Only show addons section if service has addons */}
                            {serviceOptions[serviceId as keyof typeof serviceOptions]?.addons &&
                             Array.isArray(serviceOptions[serviceId as keyof typeof serviceOptions].addons) &&
                             serviceOptions[serviceId as keyof typeof serviceOptions].addons!.length > 0 && (
                                <div className="add-ons-section">
                                    <p className="font-medium mb-2 text-sm text-italic">{t('booking.addOns')}</p>
                                    <div className="flex flex-wrap gap-2">
                                        {serviceOptions[serviceId as keyof typeof serviceOptions].addons!.map((addon, index) => {
                                            const title = extractTitle(addon);
                                            const price = extractPrice(addon);
                                            const selectedService = Array.from(formData.selectedServices)
                                                .find(s => s.title === title);

                                            return (
                                                <div key={`addon-${index}`} className="flex items-center gap-2 mb-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleServiceToggle(title, price)}
                                                        className={`px-3 py-1.5 rounded-md text-xs flex items-center ${
                                                            selectedService ? 'bg-blue-800 text-white' : 'bg-gray-100 text-gray-700'
                                                        }`}
                                                    >
                                                        <span>{formatTextWithItalics(title)} ({price})</span>
                                                    </button>
                                                    {selectedService && (
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            value={selectedService.quantity ?? 1}
                                                            onChange={e => handleQuantityChange(title, parseInt(e.target.value, 10))}
                                                            className="w-10 h-7 border border-gray-300 rounded-md text-center text-xs font-normal ml-1"
                                                            style={{ padding: 0 }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {serviceId === 'fabric' && (
                                <div className="w-full mt-2">
                                    <p className="font-medium mb-2 text-sm">{t('booking.carpet')}</p>
                                    <CarpetCalculator 
                                        basePrice={1.50} 
                                        onCalculate={handleCarpetCalculation}
                                        selectedServices={formData.selectedServices}
                                    />
                                    
                                    {/* Add Transportation Fee component for fabric services */}
                                    <TransportationFee
                                        onFeeChange={handleTransportationFee}
                                    />
                                </div>
                            )}

                            {serviceId === 'sanitise' && (
                                <div className="space-y-4">
                                    <p className="font-medium mb-2 text-sm">{t('booking.sanitiseService')}</p>
                                    <div className="flex flex-wrap gap-2">
                                        <SanitiseCalculator 
                                            basePrice={0.12} 
                                            onCalculate={handleSanitiseCalculation}
                                            premiseType={t('premises.premiseHome')}
                                            selectedServices={formData.selectedServices}
                                        />
                                        <SanitiseCalculator 
                                            basePrice={0.12} 
                                            onCalculate={handleSanitiseCalculation}
                                            premiseType={t('premises.premiseOffice')}
                                            selectedServices={formData.selectedServices}
                                        />
                                        <SanitiseCalculator 
                                            basePrice={0.12} 
                                            onCalculate={handleSanitiseCalculation}
                                            premiseType={t('premises.premiseHospital')}
                                            selectedServices={formData.selectedServices}
                                        />
                                        <SanitiseCalculator 
                                            basePrice={0.12} 
                                            onCalculate={handleSanitiseCalculation}
                                            premiseType={t('premises.premiseSchool')}
                                            selectedServices={formData.selectedServices}
                                        />
                                        <SanitiseCalculator 
                                            basePrice={0.12} 
                                            onCalculate={handleSanitiseCalculation}
                                            premiseType={t('premises.premiseOthers')}
                                            selectedServices={formData.selectedServices}
                                        />
                                    </div>
                                    
                                    {/* Add Transportation Fee component for sanitise services */}
                                    <div className="mt-3">
                                        <TransportationFee
                                            onFeeChange={handleTransportationFee}
                                        />
                                    </div>
                                </div>
                            )}

                            {serviceId === 'pest-control' && (
                                <div className="space-y-4">
                                    <p className="font-medium mb-2 text-sm">{t('booking.pestControlService')}</p>
                                    <div className="flex flex-wrap gap-2">
                                        <PestControlBooking 
                                            premiseType={t('premises.premiseHome')}
                                            onSelect={handlePestControlSelection}
                                            selectedServices={formData.selectedServices}
                                            onSelectionChange={handlePestControlSelectionChange}
                                        />
                                        <PestControlBooking 
                                            premiseType={t('premises.premiseOffice')}
                                            onSelect={handlePestControlSelection}
                                            selectedServices={formData.selectedServices}
                                            onSelectionChange={handlePestControlSelectionChange}
                                        />
                                        <PestControlBooking 
                                            premiseType={t('premises.premiseCar')}
                                            onSelect={handlePestControlSelection}
                                            selectedServices={formData.selectedServices}
                                            onSelectionChange={handlePestControlSelectionChange}
                                        />
                                        <PestControlBooking 
                                            premiseType={t('premises.premiseOthers')}
                                            onSelect={handlePestControlSelection}
                                            selectedServices={formData.selectedServices}
                                            onSelectionChange={handlePestControlSelectionChange}
                                        />
                                    </div>
                                    <PestControlNotice show={hasAnyPestControlSelection} />
                                </div>
                            )}

                            {serviceId === 'aircond' && (
                                <div className="space-y-4">
                                    <p className="font-medium mb-2 text-sm">{t('booking.aircondService')}</p>
                                    <AircondCalculator
                                        onCalculate={(services) => {
                                            setFormData(prev => {
                                                const newServices = new Set(prev.selectedServices);
                                                Array.from(newServices).forEach(service => {
                                                    if (service.title.includes(t('pricing.aircond.name'))) {
                                                        newServices.delete(service);
                                                    }
                                                });
                                                services.forEach(service => newServices.add(service));
                                                return { ...prev, selectedServices: newServices };
                                            });
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Add total price display before submit button */}
                    {formData.selectedServices.size > 0 && (
                        <div className="mt-4 border-t pt-4">
                            {/* Show service total for fabric and sanitise services */}
                            {(serviceId === 'fabric' || serviceId === 'sanitise') && (
                                <div className="flex justify-between items-center py-2">
                                    <span className="text-sm font-medium text-gray-700">{t('booking.serviceTotal')}</span>
                                    <span className="text-sm font-medium text-gray-700">
                                        {PriceCalculator.formatPriceRange(
                                            PriceCalculator.calculateTotalRange(
                                                new Set(
                                                    Array.from(formData.selectedServices).filter(
                                                        service => !service.title.includes(t('fees.title'))
                                                    )
                                                )
                                            )
                                        )}
                                    </span>
                                </div>
                            )}

                            {/* Display transportation fee for fabric and sanitise services */}
                            {(serviceId === 'fabric' || serviceId === 'sanitise') && (
                                <div className="flex justify-between items-center py-2 border-b">
                                    <span className="text-sm font-medium text-gray-700">{t('booking.transportationFee')}</span>
                                    <span className="text-sm font-medium text-gray-700">
                                        RM25.00
                                    </span>
                                </div>
                            )}

                            {/* Display final total */}
                            <div className="flex justify-between items-center py-4 mt-1">
                                <span className="text-lg font-semibold">{t('booking.total')}</span>
                                <span className="text-lg font-bold text-blue-700">
                                    {(serviceId === 'fabric' || serviceId === 'sanitise')
                                        ? (() => {
                                            // First calculate service total without transportation fee
                                            const serviceTotalRange = PriceCalculator.calculateTotalRange(
                                                new Set(
                                                    Array.from(formData.selectedServices).filter(
                                                        service => !service.title.includes(t('fees.title'))
                                                    )
                                                )
                                            );

                                            // Add transportation fee (RM25) if it exists in selected services
                                            const hasFee = Array.from(formData.selectedServices).some(service =>
                                                service.title.includes(t('fees.title')));

                                            const finalRange = {
                                                min: hasFee ? serviceTotalRange.min + 25 : serviceTotalRange.min,
                                                max: hasFee ? serviceTotalRange.max + 25 : serviceTotalRange.max,
                                                hasRange: serviceTotalRange.hasRange
                                            };
                                            return PriceCalculator.formatPriceRange(finalRange);
                                        })()
                                        : PriceCalculator.formatPriceRange(PriceCalculator.calculateTotalRange(formData.selectedServices))
                                    }
                                </span>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end mt-4">
                        <button
                            type="submit"
                            className={`${bookingConfig.submitButton.className} text-sm`}
                        >
                            {bookingConfig.submitButton.text}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BookingModal;